import React, { useContext } from "react";
import styles from "./Footer.module.css";
import { NavLink } from "react-router-dom";
import logo from "../../assets/media/company/nvig-logo-horizontal.png";
import { qrChat, qrFollow } from "../../assets/assetsExport";
import { getLang } from "../../features/app/appSlice";
import { useSelector } from "react-redux";
import { HashLink } from "react-router-hash-link";

const Footer: React.FC = () => {
  const lang = useSelector(getLang);
  return (
    <footer
      className={`${styles.footerContainer} text-white relative z-[100] bg-main-blue px-6 sm:px-10 pt-20 pb-10 text-left flex flex-col gap-16`}
    >
      <div className="content-container w-full !px-0 mx-auto flex flex-col md-lg:flex-row gap-12 md-lg:gap-10 justify-between text-sm text-white/80">
        <div className="flex flex-col gap-6">
          <NavLink to="/">
            <img className="h-8" src={logo} alt="company logo" />
          </NavLink>
          <p className="max-w-[280px] text-[13px] leading-7 text-white/60">
            {lang
              ? "Global Vision, Local Expertise. A London-based platform for international wealth, succession and growth."
              : "国际视野，本地专识。总部位于伦敦的全球财富、传承与发展平台。"}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-12 md-lg:gap-14 justify-between">
          <div className="flex flex-col gap-5">
            <p className="text-[11px] tracking-[0.3em] uppercase text-main/90">
              {lang ? "Our Businesses" : "我们的业务"}
            </p>
            <ul className="flex flex-col gap-2.5 font-medium text-[13.5px]">
              <li>
                <NavLink className="hover:text-main transition-colors" to="/business/family-office">
                  {lang ? "Family Office" : "家族办公室"}
                </NavLink>
              </li>
              <li>
                <NavLink className="hover:text-main transition-colors" to="/business/real-estate">
                  {lang ? "Real Estate" : "房地产"}
                </NavLink>
              </li>
              <li>
                <NavLink className="hover:text-main transition-colors" to="/business/technology-investment">
                  {lang ? "Technology Investment" : "科技投资"}
                </NavLink>
              </li>
              <li>
                <NavLink className="hover:text-main transition-colors" to="/business/art-culture">
                  {lang ? "Art and Culture" : "艺术与文化"}
                </NavLink>
              </li>
              <li>
                <NavLink className="hover:text-main transition-colors" to="/business/private-club">
                  {lang ? "Private Club" : "私人俱乐部"}
                </NavLink>
              </li>
              <li>
                <NavLink className="hover:text-main transition-colors" to="/business/think-tank">
                  {lang ? "Think Tank" : "智库"}
                </NavLink>
              </li>
              <li>
                <NavLink className="hover:text-main transition-colors" to="/business/cambridge-foundation">
                  {lang ? "Cambridge Foundation" : "剑桥基金会"}
                </NavLink>
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-5">
            <p className="text-[11px] tracking-[0.3em] uppercase text-main/90">
              {lang ? "About" : "关于"}
            </p>
            <ul className="flex flex-col gap-2.5 font-medium text-[13.5px]">
              <li>
                <HashLink className="hover:text-main transition-colors" to="/about#our-firm">
                  {lang ? "Our Firm" : "我们的公司"}
                </HashLink>
              </li>
              <li>
                <HashLink className="hover:text-main transition-colors" to="/about#purposes-values">
                  {lang ? "Our Purposes and Values" : "我们的宗旨和价值观"}
                </HashLink>
              </li>
              <li>
                <HashLink className="hover:text-main transition-colors" to="/about#compliance-legality">
                  {lang ? "Compliance and Legality" : "合规与合法性"}
                </HashLink>
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-5 items-start sm:items-end">
            <p className="text-[11px] tracking-[0.3em] uppercase text-main/90">
              {lang ? "Connect" : "联系"}
            </p>
            <div className="flex gap-3">
              <div>
                <img
                  className="w-16 rounded-sm"
                  src={qrChat}
                  alt={lang ? "QR code for wechat" : "微信二维码"}
                />
                <p className="text-[10px] tracking-widest uppercase text-center mt-1.5 text-white/60">
                  Chat with Us
                </p>
              </div>
              <div>
                <img
                  className="w-16 rounded-sm"
                  src={qrFollow}
                  alt={lang ? "QR code for wechat" : "微信二维码"}
                />
                <p className="text-[10px] tracking-widest uppercase text-center mt-1.5 text-white/60">
                  Follow Us
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-1.5 mt-4 text-[13px] leading-relaxed text-white/75">
              <p className="flex flex-col items-start sm:items-end">
                <span>16 Berkeley Street,</span>
                <span>Mayfair, London</span>
                <span>W1J 8DZ, UK</span>
              </p>
              <p className="mt-2">+44 (0)20 3907 4025</p>
              <p>
                <a className="hover:text-main transition-colors" href="mailto:info@newvgroup.com">info@newvgroup.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="content-container w-full !px-0 mx-auto border-t border-white/15 pt-6 text-xs flex flex-col md:flex-row gap-4 md:gap-2 justify-between text-secondary-light">
        <div>
          <p className="opacity-60 text-[11px] tracking-wider">
            &copy;{new Date().getFullYear()}{" "}
            {lang
              ? "New Vision Group. All Rights Reserved."
              : "新愿景集团 版权所有"}
          </p>
          <p className="opacity-40 text-[10px] mt-1.5 leading-relaxed">
            New Vision International Group Limited
            <br />
            {lang
              ? "Registered in England and Wales, Company No. 13029696."
              : "新愿景国际集团有限公司，注册于英格兰及威尔士，公司编号为13029696。"}
          </p>
        </div>
        <div className="flex gap-6 opacity-70 text-[11px] tracking-wider">
          <NavLink className="hover:text-main transition-colors" to="/policies/terms-and-conditions">
            {lang ? "Terms & Conditions" : "条款与条件"}
          </NavLink>
          <NavLink className="hover:text-main transition-colors" to="/policies/cookies-policy">
            {lang ? "Cookies Policy" : "Cookie政策"}
          </NavLink>
          <NavLink className="hover:text-main transition-colors" to="/policies/privacy-policy">
            {lang ? "Privacy Policy" : "隐私政策"}
          </NavLink>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
